/**
 * The checkout and receipt copy, and every decision either screen makes (`MEM-020`).
 *
 * `apps/web` can render components under `node:test` now (ADR 043), so this file is no longer the
 * only place a fact can be asserted — but the countdown arithmetic below belongs here anyway, and
 * for a better reason than testability: it is the one piece of logic on these screens that can be
 * *wrong* rather than merely ugly, and a member watching a timer is watching the thing the seat
 * hold is for.
 */

import type { MemberOrderStatus, MemberRegistrationStatus } from './types.js';

/**
 * Where the checkout for one event lives — and, once a member has chosen one, which ticket.
 *
 * ## The selection is on the URL, and that is a decision rather than a convenience
 *
 * The seat hold does **not** carry the ticket type. `EVT-022`'s hold holds *a seat*, capacity is
 * per event (`EVT-020`), and a hold row that knew its tier would be the first field of the
 * per-type inventory `EVT-019` AC-3 forbids — no hold-back, no pool, no quota. So the choice has
 * to live somewhere the member owns, and the address they are looking at is the honest place: a
 * reload, a back button and a pasted link all come back to the ticket they were actually buying,
 * and the platform holds no opinion about how many VIP seats are left.
 *
 * Omitted for the organizer's first ticket type, which is what the pinned reserve bar names and
 * prices (`MEM-020` AC-1) — so the address a member reaches from that bar is the address it has
 * always been, and a single-ticket event never grows a query string.
 */
export function checkoutPath(eventId: string, ticketTypeId?: string | null): string {
  const path = `/app/events/${encodeURIComponent(eventId)}/checkout`;
  return ticketTypeId === undefined || ticketTypeId === null
    ? path
    : `${path}?${TICKET_TYPE_PARAMETER}=${encodeURIComponent(ticketTypeId)}`;
}

/** The search parameter the checkout reads its selection from. Spelled once, read in two places. */
export const TICKET_TYPE_PARAMETER = 'ticket';

/** Where the receipt for one event lives. */
export function receiptPath(eventId: string): string {
  return `/app/events/${encodeURIComponent(eventId)}/receipt`;
}

/**
 * **Where a signed-out visitor's reserve action sends them** (`MEM-021` AC-6).
 *
 * > A signed-out visitor selecting reserve is taken through account creation and profile
 * > completion before payment, then returned to payment **for the same event**.
 *
 * `/app/join` is `MEM-001`'s wizard, and it is both halves of that: step 1 creates the account
 * (`MEM-002`) and steps 2 to 5 are the profile. `returnTo` is `MEM-001` AC-6's own mechanism —
 * carried through account creation, stored on the registration record, and navigated to when the
 * wizard completes — so the destination survives the five steps and the sign-in in between.
 *
 * **The destination is the checkout for this event and nothing more general.** Sending somebody to
 * `/app` or to the event page would satisfy "returned" and lose the thing AC-6 is about: a
 * visitor's first contact is usually an event link and the reserve button is where they decide to
 * join, so *"losing the event between account creation and payment turns an intent into a
 * search"*.
 *
 * No seat is held on the way. Holding one would take a seat off the room for fifteen minutes for
 * somebody who has not finished creating an account, and `MEM-019` AC-2 keeps registration itself
 * off a signed-out visitor's page — what this offers is the path to becoming a member, not a
 * registration.
 */
export function joinThenCheckoutPath(eventId: string): string {
  return `/app/join?returnTo=${encodeURIComponent(checkoutPath(eventId))}`;
}

/**
 * **Which event, and which ticket, a sign-up is on its way to** (`MEM-058` AC-5).
 *
 * The inverse of {@link joinThenCheckoutPath}: the registration wizard carries nothing about an
 * event but its `returnTo`, and when that is an event's checkout the event and the ticket are in
 * it. `null` for any other destination (Get started, a sign-in redirect, an app page), which is a
 * sign-up that is not for an event and draws no summary beside it. The ticket is `null` when the
 * address names none, which is the organizer's first ticket type (see {@link checkoutPath}).
 */
export function checkoutTargetOf(
  returnTo: string | null | undefined,
): { readonly eventId: string; readonly ticketTypeId: string | null } | null {
  if (typeof returnTo !== 'string' || !returnTo.startsWith('/')) return null;
  const url = new URL(returnTo, 'http://h2hspark.invalid');
  const match = /^\/app\/events\/([^/]+)\/checkout\/?$/u.exec(url.pathname);
  if (match === null) return null;
  const eventId = decodeURIComponent(match[1]!);
  if (eventId.length === 0) return null;
  const ticketTypeId = url.searchParams.get(TICKET_TYPE_PARAMETER);
  return {
    eventId,
    ticketTypeId: ticketTypeId === null || ticketTypeId === '' ? null : ticketTypeId,
  };
}

/** The summary's fee and total at sign-up for a paid ticket, before one is held (`MEM-058` AC-6). */
export const SIGN_UP_SUMMARY_SETTLED_AT_PAYMENT = 'At payment';

/** The summary's two rows below the ticket, labelled as the checkout's own itemisation labels them. */
export const SIGN_UP_SUMMARY_FEE_LABEL = 'Service fee';
export const SIGN_UP_SUMMARY_TOTAL_LABEL = 'Total';

/** One row of the sign-up summary's lines: what it is, what it is called, and what it reads. */
export interface SignUpSummaryRow {
  readonly kind: 'ticket' | 'service-fee' | 'total';
  readonly label: string;
  readonly amount: string;
  /** `true` when the amount is the placeholder, which is drawn muted as the checkout's is not. */
  readonly settledAtPayment: boolean;
}

/**
 * **The lines under the sign-up card, decided the way the checkout decides them** (`MEM-058` AC-6).
 *
 * The ticket is the one the reserve action named, or the organizer's first when it named none.
 *
 * | The ticket is | Rows |
 * | --- | --- |
 * | paid | the ticket at its price, then Service fee and Total, both *At payment* |
 * | free (a zero-priced tier) | the ticket at its price, then Total at that same price |
 * | none at all (a free event) | the event's `freeAdmission` (General admission, `$0.00`), then Total at that price |
 *
 * **A free order has no Service fee row, and no row says *At payment*.** The server itemises a free
 * order as the ticket and the total with no fee line (`itemiseOrder`, `MEM-050` AC-5), and Sumit's
 * ruling of 2026-09-04 is *"free events unchanged (no fee line, no card)"*. A card that promised a
 * fee settled at payment on an event with no payment told the visitor something the checkout then
 * contradicted (TW 44206238). The branch is on the ticket being free (`priceCents === 0`, the same
 * test `computeOrderFees` uses to leave a ticket out of `paidTicketCount`) and never on a fee.
 *
 * On a paid ticket the fee and the total are still the server's to compute at the checkout, so they
 * stay placeholders in their places. Nothing here adds, rounds or formats: every amount is a
 * string the API rendered. A free event has no ticket type to carry one, so the public event
 * carries `freeAdmission`, the name and price its order will be itemised under.
 */
export function signUpSummaryRows(
  ticketTypes: ReadonlyArray<{
    readonly ticketTypeId: string;
    readonly name: string;
    readonly priceCents: number;
    readonly price: string;
  }>,
  ticketTypeId: string | null,
  freeAdmission: { readonly name: string; readonly price: string } | null | undefined,
): ReadonlyArray<SignUpSummaryRow> {
  const ticket = ticketTypes.find((type) => type.ticketTypeId === ticketTypeId) ?? ticketTypes[0];
  if (ticket === undefined) {
    // No ticket types and no `freeAdmission` is not an event the API serves (a paid event always
    // has a ticket type). Nothing is drawn rather than a price this app would have to make up.
    if (freeAdmission == null) return [];
    return [
      {
        kind: 'ticket',
        label: freeAdmission.name,
        amount: freeAdmission.price,
        settledAtPayment: false,
      },
      {
        kind: 'total',
        label: SIGN_UP_SUMMARY_TOTAL_LABEL,
        amount: freeAdmission.price,
        settledAtPayment: false,
      },
    ];
  }
  const ticketRow: SignUpSummaryRow = {
    kind: 'ticket',
    label: ticket.name,
    amount: ticket.price,
    settledAtPayment: false,
  };
  if (ticket.priceCents === 0) {
    return [
      ticketRow,
      {
        kind: 'total',
        label: SIGN_UP_SUMMARY_TOTAL_LABEL,
        amount: ticket.price,
        settledAtPayment: false,
      },
    ];
  }
  return [
    ticketRow,
    {
      kind: 'service-fee',
      label: SIGN_UP_SUMMARY_FEE_LABEL,
      amount: SIGN_UP_SUMMARY_SETTLED_AT_PAYMENT,
      settledAtPayment: true,
    },
    {
      kind: 'total',
      label: SIGN_UP_SUMMARY_TOTAL_LABEL,
      amount: SIGN_UP_SUMMARY_SETTLED_AT_PAYMENT,
      settledAtPayment: true,
    },
  ];
}

// ─── the pinned reserve bar (`MEM-020` AC-1, AC-6) ─────────────────────────────────────────────

/**
 * `MEM-020` AC-1 and AC-6, which are one function because they are one control.
 *
 * AC-1: *"a pinned action bar at the foot of the event detail page reading **"Reserve my spot ·
 * $25"** with the ticket type"*. AC-6: *"a free event's reserve action reads **"Reserve my spot ·
 * Free"**"*.
 *
 * Two acceptance criteria, one label, one place it is spelled. Writing them as two constants in
 * two components is how a wording change lands on one of them — and the two differ by exactly one
 * word, which is the case a reviewer's eye slides over.
 *
 * **The price is the server's rendering, not the client's.** `price` is `PublicTicketType.price`,
 * which `formatCents` produced by integer division on the API. This function concatenates; it
 * never formats an amount, which is why "· $25" is a byte a test may assert rather than something
 * that depends on which Node version rendered it.
 *
 * `null` means free — that is how `memberFacingTicketTypes` answers for an event with no ticket
 * types, which `EVT-017` AC-2 makes every free event.
 */
export function reserveActionLabel(price: string | null): string {
  return `Reserve my spot · ${price ?? FREE_PRICE_WORD}`;
}

/** What stands where a price would on a free event. `MEM-TKT-005`'s own word. */
export const FREE_PRICE_WORD = 'Free';

/**
 * `MEM-020` AC-1's *"quantity is **fixed at one**"*, said to the member.
 *
 * The specification's correction is why this is a sentence rather than a control: *"the organizer
 * surface renders 'Max per person' as a select with the value '1 ticket', implying
 * configurability. Quantity is fixed at one in MVP and the control is a static statement, not a
 * choice."* A stepper stuck at one, or a disabled quantity field, is the same lie in the member's
 * app — it offers a decision the platform will not honour.
 */
export const ONE_TICKET_PER_PERSON = 'One ticket per person.';

/** The ticket type named beside the price on the pinned bar (`MEM-020` AC-1). */
export function reserveTicketTypeLine(ticketTypeName: string): string {
  return `${ticketTypeName} · ${ONE_TICKET_PER_PERSON}`;
}

// ─── the checkout screen ───────────────────────────────────────────────────────────────────────

/**
 * The checkout's title — **"Reserve Your Spot"**, in title case, as Brad asked for it on dev (TW
 * 44132872, https://ten243.teamwork.com/app/tasks/44132872) and Sumit approved on 2026-09-18.
 *
 * The prototype's subheading under it — the event and when it is — is gone in the same change: the
 * summary card beside it names the event, its date and its hours, and a second statement of the
 * same facts made the reader check whether the two were different.
 */
export const CHECKOUT_HEADING = 'Reserve Your Spot';

/**
 * **The terms section's title**, on the checkout and on the confirmation (TW 44132872).
 *
 * It holds the event's refund policy under {@link REFUND_POLICY_HEADING} and, on a paid order, the
 * served fee disclosure. On a free checkout it sits below the reserve action, as it does on the
 * confirmation; on a paid one it stays above the Pay control — `MEM-058` AC-3 and `MEM-050` AC-2
 * require the terms and the disclosure to be read before the control that takes money.
 */
export const EVENT_TERMS_HEADING = 'Event Terms and Conditions';

export const ORDER_SUMMARY_LABEL = 'Order summary';

/**
 * The sentence under the summary's totals (`Web App.dc.html` §3.1, line 1093), verbatim.
 *
 * Ruling 62 kept §3.1's summary card *"with every figure, sentence and behaviour unchanged"*, and
 * named four departures; this line is not one of them. It was simply missing, so the card said what
 * the seat costs and never what the seat is for. Restored in the parity pass, 2026-09-12 — a
 * `--purple-50` note with a `sparkles` glyph, under the total, as the design draws it on every
 * step of the flow.
 */
export const ORDER_SUMMARY_INCLUDES_NOTE = 'Includes pre-event matches & follow-up openers';

/**
 * **The reservation flow's own steps** (`Web App.dc.html` §3.1, the stepper above the step body).
 *
 * ## Why there are two of them and not the prototype's three
 *
 * §3.1 steps *Account → Profile → Payment*, because the flow it draws is a **sign-up funnel**: a
 * visitor who pressed Reserve on the public site and does not have an account yet. Ours is not
 * that. The checkout route is gated, so by the time anybody sees this stepper they have a session
 * — a visitor is sent to `/app/join` from the event page's reserve bar instead (`MEM-021` AC-6),
 * which is a different flow with its own screens.
 *
 * Drawing *Account ✓ → Profile ✓ → Payment* to a member who signed up two years ago would be a
 * progress indicator that had never made any progress. So the steps are the two this flow actually
 * has, and both are real addresses a member walks between: the checkout page, then the
 * confirmation. `Reserve` rather than `Payment` because the same two steps carry a free event,
 * which pays nothing.
 *
 * ## Why a stepper at all, on a flow of two
 *
 * Because the second step is the one that matters: a member holding a seat for fifteen minutes,
 * about to be charged, should be able to see that something comes after this. §3.1's whole
 * treatment is built on saying where you are in a thing you have not finished.
 */
export const CHECKOUT_STEPS = [
  { id: 'reserve', label: 'Reserve' },
  { id: 'confirmed', label: 'Confirmed' },
] as const;

export type CheckoutStepId = (typeof CHECKOUT_STEPS)[number]['id'];

/**
 * **The card surface every block of the reserve flow sits on** (`Web App.dc.html` §3.1).
 *
 * `--surface-card` on the ivory ground, a `--border-subtle` hairline, `--radius-xl` and
 * `--shadow-sm` — the same treatment §3.1 gives its confirmed panel and §3.4 gives the organizer's
 * cards, which is why it is the house surface rather than this screen's invention.
 *
 * One constant rather than the string written five times, because the failure mode is not that
 * somebody picks a different radius on purpose: it is that four of five blocks get updated and the
 * fifth is the one nobody scrolled to. The three panels that use it live in their own files.
 *
 * Padding but no vertical rhythm: each block's internal spacing is its own, and a `space-y` baked
 * in here would be wrong for the one that has a grid in it.
 */
export const CHECKOUT_SECTION_CARD =
  'rounded-xl border border-border-subtle bg-card p-4 shadow-sm sm:p-5';

/** The stepper's accessible name — it is a list of where you are, not decoration. */
export const CHECKOUT_STEPS_LABEL = 'Reservation progress';

/** Announced on the step already behind you, so a check mark is not the only way to know. */
export const CHECKOUT_STEP_DONE_SUFFIX = 'done';

/** Announced on the step you are on. */
export const CHECKOUT_STEP_CURRENT_SUFFIX = 'current step';

/**
 * The region the served fee-forfeit disclosure is drawn in (`MEM-050` AC-2, AC-3).
 *
 * A test id and **no copy**: the sentence is the server's (`feeDisclosure` on the checkout, the
 * confirmation and the order record) and this app holds none of its own, which is AC-4. The id is
 * what lets a test find the region on three screens and assert the three render one sentence.
 */
export const SERVICE_FEE_DISCLOSURE_TEST_ID = 'service-fee-disclosure';
export const PAYMENT_SECTION_HEADING = 'Payment method';
/**
 * **The confirmation's card** — `Web App.dc.html` §3.1's `resDone`, on the free and the paid
 * confirmation alike (TW 44132582, https://ten243.teamwork.com/app/tasks/44132582).
 *
 * The heading is the prototype's, verbatim. The sentence is the prototype's with two things taken
 * out because they would not be true:
 *
 *   - *"We've emailed your **ticket** and order confirmation"* — the confirmation mail
 *     (`registration-confirmation`, sent on the free and the paid path) carries the ticket type and
 *     the order number. There is no ticket in it: the ticket is in the app, behind *View ticket* on
 *     the event page, which is where {@link GO_TO_EVENT_LABEL} goes.
 *   - *"Your matches unlock **48 hours** before the event"* — the release interval is set per event
 *     (`MEM-MAT-002`, `MEM-024` AC-2), so the interval is the one phrase not taken verbatim, the
 *     same edit `PER_EVENT_LOCKED` makes for the same reason.
 */
export const RECEIPT_HEADING = "You're going! ✦";

/** The confirmed card's sentence. See {@link RECEIPT_HEADING} for the two words it drops. */
export const RECEIPT_LEAD =
  "We've emailed your order confirmation. Your matches unlock shortly before the event.";

/**
 * The confirmed card's one action, to the event's page — in Brad's words (TW 44132582) where the
 * prototype has *See my matches*, which `handoff-corrections.md` #155 already declined: matches
 * open shortly before the event, so on the day somebody reserves it is a page with nothing on it.
 * The event page is where the ticket, Who's coming and, later, the matches are.
 */
export const GO_TO_EVENT_LABEL = 'Go To Event';
export const REFUND_POLICY_HEADING = 'Refunds';
export const BACK_TO_EVENT_LABEL = 'Back to event';

/** `MEM-020` AC-6's reserve action, on a checkout that owes nothing. */
export const CONFIRM_FREE_RESERVATION_LABEL = 'Confirm my spot';

/**
 * What a member reads when their hold has run out (`MEM-020` AC-2, `EVT-022` AC-3).
 *
 * It does not say the event is full, because it is not known to be: the seat went back to
 * availability and may well still be there. `EVT-022`'s own refusal for a genuinely expired hold
 * takes the same line — *"Your seat is no longer being held. Start again to see if one is free."*
 */
export const HOLD_EXPIRED_NOTICE =
  'Your seat is no longer being held. Start again to see if one is free.';

export const START_AGAIN_LABEL = 'Start again';

/**
 * **The two sentences that make a blank reserve screen impossible** — TW 44159509
 * (https://ten243.teamwork.com/app/tasks/44159509).
 *
 * Brad reserved as a brand-new account and got the member shell, a *Back to event* link and one
 * empty grey box. The screen has a state for *no checkout in hand*, and that state drew a bare
 * `Skeleton` — a promise that something is coming, in a state where nothing was.
 *
 * So the state is split in two and both halves carry words. While the `POST` that takes the seat is
 * out, something **is** coming, and the screen says what; when nothing is in flight and there is
 * still no checkout, the screen says no seat is being held and offers {@link START_AGAIN_LABEL}.
 *
 * Neither says the event is full, for {@link HOLD_EXPIRED_NOTICE}'s reason: only the server knows
 * whether there is a seat, and the honest statement here is about **this reservation**, not about
 * the room.
 */
export const HOLDING_A_SEAT_NOTICE = 'Holding a seat for you…';

/** See {@link HOLDING_A_SEAT_NOTICE}. */
export const RESERVATION_DID_NOT_START_NOTICE =
  'We could not start holding a seat for you. Nothing has been reserved.';

// ─── the countdown (`MEM-020` AC-2, `EVT-022` AC-2) ───────────────────────────────────────────

/**
 * How many seconds are left on a hold, **as of now**, from the two facts the server serves.
 *
 * ## Why both inputs are used, and neither alone
 *
 * `EVT-022` serves `secondsRemaining` (the truth as of the response) and `expiresAt` (the
 * instant). `checkout-hold.ts`'s header says what each is for and what happens if a screen picks
 * one:
 *
 *   - **`secondsRemaining` minus elapsed time alone** is wrong for a tab that was asleep. A phone
 *     that locks for ten minutes stops firing timers; on waking, a naive tick counter has counted
 *     down by however many ticks it managed and shows a member four minutes on a seat that went
 *     back six minutes ago.
 *   - **`expiresAt` minus the device clock alone** is wrong for a device whose clock is wrong. A
 *     phone twenty minutes fast shows an expired hold; one twenty minutes slow shows a hold that
 *     lasts thirty-five.
 *
 * So this takes the **smaller** of the two, which is the safe direction in both failure modes: the
 * countdown can under-state the time left and can never over-state it. A member who is told they
 * have less time than they do goes faster; one who is told they have more loses a seat mid-form.
 *
 * Floored at zero, so a screen never renders a negative duration.
 */
export function secondsLeftOnHold(input: {
  /** `SeatHold.secondsRemaining`, as served. */
  readonly servedSecondsRemaining: number;
  /** Milliseconds of wall time since that response was received. */
  readonly elapsedMs: number;
  /** `SeatHold.expiresAt`. */
  readonly expiresAt: string;
  /** The device clock, in milliseconds. Passed in so this stays a pure function. */
  readonly now: number;
}): number {
  const sinceResponse = input.servedSecondsRemaining - Math.floor(input.elapsedMs / 1000);
  const expiry = Date.parse(input.expiresAt);
  const byTheClock = Number.isNaN(expiry) ? sinceResponse : Math.floor((expiry - input.now) / 1000);
  return Math.max(0, Math.min(sinceResponse, byTheClock));
}

/**
 * `900` → `15:00`; `62` → `1:02`; `0` → `0:00`.
 *
 * **Integer arithmetic and string concatenation, never `Intl` and never a `Date`.** The rule
 * `formatCents` follows for money, applied to a duration for the same reason: `.nvmrc` pins Node
 * 22.11 and dev machines run 22.22, and an `Intl`-formatted string is not a byte a test may
 * assert. `new Date(ms).toISOString().slice(14, 19)` would also be wrong the moment a hold could
 * exceed an hour.
 *
 * Minutes are not zero-padded and seconds always are, which is how a clock reads.
 */
export function formatCountdown(secondsRemaining: number): string {
  const total = Math.max(0, Math.floor(secondsRemaining));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${String(minutes)}:${seconds < 10 ? `0${String(seconds)}` : String(seconds)}`;
}

/**
 * `MEM-TKT-002`'s countdown line, verbatim: *"Your seat is held for 14:32"*.
 *
 * The specification marks the copy "(copy TBC)". It is used as written until somebody rules
 * otherwise, and it is one function so that ruling is a one-line change rather than a search.
 */
export function seatHeldLine(secondsRemaining: number): string {
  return `Your seat is held for ${formatCountdown(secondsRemaining)}`;
}

/**
 * How often the countdown re-renders.
 *
 * One second, because `MEM-TKT-002` says *"updating each second"*. Named rather than inlined so
 * the interval and the thing it is an interval *of* are visibly the same number.
 */
export const COUNTDOWN_TICK_MS = 1000;

// ─── what the two conditional regions say, and who says it ────────────────────────────────────

/*
 * **Every sentence in those regions is served, and none is written here.**
 *
 * `Checkout.paymentNotice` is what stands where the card form will go on a paid checkout, and it
 * is `null` on one that owes nothing — so a free member is shown no payment section at all rather
 * than an explanation of an absence they have no reason to wonder about (`MEM-020` AC-6).
 *
 * `refundPolicy.sentence` is `EVT-024`'s own rendering of the terms, produced by the one
 * `describeRefundPolicy` that also writes the listing's; `refundPolicyNotice` is what stands there
 * when an event has none, and it is one of **two** sentences, because "this event is free, so
 * there is nothing to refund" and "the organizer has not set terms" are different facts and only
 * the server knows which applies. The screens read `refundPolicy?.sentence ?? refundPolicyNotice`
 * and compose nothing.
 *
 * All of it arrives on the payload for the reason every other sentence in this product does: one
 * sentence, one source, and the sentence a test asserts is the sentence a member gets.
 *
 * None of them is a **disabled control**, deliberately. A greyed-out Pay button reads as a
 * transient failure somebody would come back and retry; a sentence reads as a fact about the
 * product. The headings over them are copy this screen owns, and they are the two constants
 * above.
 */

// ─── the payment section (`MEM-021` AC-1, AC-2) ───────────────────────────────────────────────

/**
 * `MEM-021` AC-1's saved-card line, verbatim: *"Visa ending 4242"*.
 *
 * The brand is the provider's own key — `visa`, `mastercard`, `amex` — and {@link CARD_BRAND_LABELS}
 * turns it into the word a member reads. An unknown network renders its own key capitalised rather
 * than a placeholder: a member who paid with a network this map has not met should still recognise
 * their own card, and "Card ending 4242" would read as though the platform had lost track of it.
 *
 * String concatenation and no `Intl`, the rule this file already keeps for the countdown and the
 * price: `.nvmrc` pins Node 22.11, dev machines run 22.22, and an `Intl`-formatted string is not a
 * byte a test may assert.
 */
export function savedCardLine(brand: string, last4: string): string {
  return `${cardBrandLabel(brand)} ending ${last4}`;
}

/**
 * `MEM-021` AC-1's second line, verbatim: *"Expires 08/28 · saved"*.
 *
 * Zero-padded month and the last two digits of the year, by integer arithmetic and string
 * slicing — never a `Date`, which would turn a month and a year into an instant with a timezone
 * and then render it in the reader's.
 *
 * **The word "saved" is doing a specific job and it is honest.** This platform holds no wallet:
 * `PAY-001` AC-4 and ADR 045 refuse it any way to list, add or delete a stored payment method. The
 * card is saved *with the provider*, and what H2HSpark holds is the four display values off a
 * payment this member already made. Paying with it still goes through the provider's own fields.
 */
export function savedCardExpiryLine(expiryMonth: number, expiryYear: number): string {
  const month = expiryMonth < 10 ? `0${String(expiryMonth)}` : String(expiryMonth);
  const year = String(expiryYear).slice(-2);
  return `Expires ${month}/${year} · ${SAVED_CARD_WORD}`;
}

/** The last word of AC-1's line, named once so a ruling on the copy is one edit. */
export const SAVED_CARD_WORD = 'saved';

/**
 * The word a member reads for each network the provider reports.
 *
 * The provider's keys are lower-case identifiers and a member's card says `VISA`; neither is what
 * belongs on a screen. `MEM-021` AC-1 quotes "Visa", so that is the spelling.
 */
export const CARD_BRAND_LABELS: Readonly<Record<string, string>> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  amex: 'American Express',
  discover: 'Discover',
  diners: 'Diners Club',
  jcb: 'JCB',
  unionpay: 'UnionPay',
};

/** The brand as a member reads it, or the provider's own key capitalised. See {@link savedCardLine}. */
export function cardBrandLabel(brand: string): string {
  const known = CARD_BRAND_LABELS[brand.toLowerCase()];
  if (known !== undefined) return known;
  return brand.length === 0 ? 'Card' : `${brand.charAt(0).toUpperCase()}${brand.slice(1)}`;
}

/** The heading over the list of cards this member has paid with (`MEM-021` AC-1). */
export const SAVED_CARDS_HEADING = 'Your cards';

/** The heading over the provider's own fields (`MEM-021` AC-2). */
export const NEW_CARD_HEADING = 'Pay with a new card';

/**
 * The pay action, with the amount on it.
 *
 * The amount is the **server's** rendering — the total line's `amount`, produced by `formatCents`
 * — so this concatenates and never formats. The same discipline `reserveActionLabel` keeps, and
 * for the same reason: a member reads one number in two places and they must be one number.
 */
export function payActionLabel(total: string): string {
  return `Pay ${total}`;
}

/** What the button says while the provider is deciding. Not a spinner alone: a member who has just
 * handed over a card needs to be told the platform is waiting rather than idle. */
export const PAYING_LABEL = 'Taking payment…';

/**
 * What a member reads under the card form.
 *
 * `SYS-018`'s posture, said to the person it protects. It is a statement of fact rather than a
 * reassurance: the fields above belong to the payment provider, and what this application receives
 * is a reference.
 */
export const CARD_ENTRY_ASSURANCE =
  'Your card details go straight to our payment provider. H2HSpark never sees or stores them.';

// ─── the provider's hosted card fields (`MEM-021` AC-2, AC-3) ─────────────────────────────────

/*
 * **The provider's hosted card fields, and the contract that keeps their contents out of this
 * application** (`MEM-021` AC-2, AC-3).
 *
 * ## What AC-3 actually asks for, and why a field list is not enough
 *
 * > **Card details are captured by the provider's hosted elements in the member's browser and
 * > exchanged directly with the provider**: no card number, expiry, CVC or PAN reaches an
 * > H2HSpark server, log, database, backup, error trace or monitoring payload.
 *
 * The failure mode is never a decision. Nobody sets out to post a card number. What happens is an
 * `onChange` that keeps the value in React state "to validate it", a `console.log` while debugging
 * a decline, or an error tracker that captures a form's serialised contents on the exception three
 * lines later. Every one of those is a small, reasonable-looking edit, and every one of them puts
 * a PAN somewhere H2HSpark is then responsible for.
 *
 * So the defence is that **the values are never available to this application at all.** The
 * provider's elements are iframes on the provider's own origin; the platform's script cannot read
 * across that boundary. What this file declares is the shape of what we may ask them to do, and
 * the shape has no way to answer with a value:
 *
 *   - {@link CardElementsHost.mount} is handed a DOM node and a field name. It returns nothing.
 *   - {@link CardElementsHost.createPaymentMethodToken} returns a **token** — a reference the
 *     provider minted, naming an instrument the provider holds.
 *   - {@link CardElementsHost.onValidityChange} reports *whether* the fields are complete and a
 *     message the provider wrote about them. Never a value, never a partial one.
 *
 * There is no `getValue`, no `read`, no `serialise`. A future edit that wants one has to add a
 * method to this interface, which is a diff a reviewer sees, and
 * `apps/web/__tests__/event-orders/no-card-detail-leaves-the-browser.test.ts` fails the build if a
 * chargeable identifier appears anywhere in this slice.
 *
 * ## Why the field vocabulary lives here and nowhere else
 *
 * `MEM-021` AC-2 fixes the fields: *"the card form collects card number, expiry (MM / YY) and
 * CVC; the web form additionally collects the name on the card"*. Those four are the only place in
 * `apps/web` where the words appear, and they appear as **mount points** — the names of regions
 * the provider draws into — rather than as inputs, values or state. The scan that enforces AC-3
 * exempts this one file for exactly that reason and asserts, in exchange, that nothing here holds
 * a value or makes a request.
 *
 * **The name on the card is a provider field too, and that is not an optimisation.** It is on the
 * forbidden side of `PAY-001`'s line — *"the name on the card"* sits in the same column as the PAN
 * and the security code in `no-card-data-crosses-the-port.test.ts` — so it cannot be an ordinary
 * `<input>` whose value this application holds, however harmless a name looks beside a card
 * number. It is drawn by the provider like the other three.
 */

/**
 * The four fields a member fills in, in the order they are drawn (`MEM-021` AC-2).
 *
 * A tuple, so adding a fifth is an edit to this line — the same arrangement
 * `LEDGER_TRANSACTION_TYPES` and `EVENT_ORDER_STATUSES` use, and for the same reason: a
 * vocabulary that can grow anywhere grows somewhere nobody is looking.
 */
export const CARD_ELEMENT_FIELDS = ['number', 'expiry', 'security-code', 'name'] as const;

export type CardElementField = (typeof CARD_ELEMENT_FIELDS)[number];

/**
 * What each field is labelled, and what a member is told to type.
 *
 * The labels sit outside the iframe — they are H2HSpark's chrome around the provider's input — so
 * they are this application's copy. The placeholder wording is the specification's: *"expiry
 * (MM / YY)"*.
 */
export const CARD_FIELD_LABELS: Readonly<Record<CardElementField, string>> = {
  number: 'Card number',
  expiry: 'Expiry (MM / YY)',
  'security-code': 'CVC',
  name: 'Name on card',
};

/**
 * Which fields this surface draws.
 *
 * All four. `MEM-021` AC-2 distinguishes the PWA's three from *"the web form"*, which
 * *"additionally collects the name on the card"*, and `apps/web` **is** the web application — the
 * member app is served from it at `/app`. Drawing three here and four somewhere else would be two
 * card forms, which is the one arrangement AC-3 makes expensive: two places for a value to be
 * read.
 */
export const WEB_CARD_FIELDS: ReadonlyArray<CardElementField> = CARD_ELEMENT_FIELDS;

/**
 * What stands where the provider's elements would when none is configured.
 *
 * `PAYMENT_PROVIDER` is unset in every deployed environment until an operator sets it (ADR 045),
 * and a checkout in that state must not draw a card form that cannot work. It refuses rather than
 * pretending, which is the same position `noPaymentProviderIsConfigured` takes on the server: ADR
 * 045's own rule is that *"an adapter that looks like it took the money is worse than one that
 * crashes"*, and a form that looks like it took a card is the client-side shape of that.
 */
export const NO_CARD_ELEMENTS_AVAILABLE =
  'Card payment is not available right now. Your seat is being held while you are here, and it goes back when the timer runs out.';

/**
 * What the development stand-in draws where a provider's field would be.
 *
 * A sentence, not an input. `api.ts`'s `createMockCardElements` header carries the argument: a
 * stand-in that drew inputs would be inputs whose values this application holds, which is the one
 * thing `MEM-021` AC-3 forbids and the one thing the real integration never does.
 *
 * **Three words, since the field boxes became §3.1's 52px.** It used to read *"Test mode — no card
 * is collected here."*, which wrapped to two lines inside a one-line control at 375 and was cut
 * off by it. The provider's real field is one line by construction, so the stand-in has to be one
 * line too or the phone layout is only ever reviewed against a shape that will not ship. What it
 * says is unchanged — `MOCK_CARD_ENTRY_NOTICE` below carries the full sentence once, under the
 * three fields, where it is read rather than repeated.
 */
export const MOCK_CARD_FIELD_NOTICE = 'Test mode';

/** What the stand-in reports where the provider's own validation message would go. */
export const MOCK_CARD_ENTRY_NOTICE =
  'Payments are in test mode. No card is collected and no money moves; the rest of the checkout is real.';

/**
 * The token the stand-in answers with.
 *
 * `@h2hspark/payments`' mock adapter takes any token, so the value only has to be recognisable in
 * a log. It is a reference to nothing, which is exactly what a real token is to this application.
 */
export const MOCK_PAYMENT_METHOD_TOKEN = 'pm_tok_mock_development';

// ─── the real provider's elements (`PAY-004`) ─────────────────────────────────────────────────

/**
 * Where the provider's script comes from.
 *
 * Fetched on first `mount` rather than from `index.html`, so a visitor browsing events never
 * pays for a payment SDK they will not use. The versioned path is the provider's own supported
 * entry point and is the only third-party origin the member app requests at runtime; a
 * content-security-policy narrowing later has one constant to name rather than a grep.
 */
export const STRIPE_SCRIPT_URL = 'https://js.stripe.com/v3/';

/*
 * The map from this application's four regions to the provider's own field types lives in
 * `stripe-card-elements.ts` and deliberately not here. Naming the provider's element types means
 * writing the provider's spelling of "card number", and the AC-3 source scan in
 * `the-payment-method-a-member-uses.test.tsx` refuses that token everywhere in this slice — as it
 * should, since the token is otherwise only ever a variable holding one. Keeping it in the single
 * file that has an argued exemption keeps the scan's blast radius at one file instead of two.
 */

/**
 * How the provider's fields are told to draw themselves.
 *
 * The iframe's contents are the provider's, so this is the only influence this application has
 * over them — and it has to be exercised, or four provider-default inputs sit inside four
 * H2HSpark-styled boxes and the row looks broken in the one place a member is most alert.
 *
 * **The values are literals, and they have to be, which is the uncomfortable part.** Every other
 * colour in this slice is a token — `text-body`, `text-subtle`, `destructive` — resolved from a
 * CSS custom property on this document's `:root`. The provider's stylesheet runs inside an iframe
 * on the provider's origin and cannot see that `:root`, so a `var(--text-body)` here resolves to
 * nothing and the field falls back to the provider's default. Literals are the only thing that
 * crosses.
 *
 * So each one below is the **resolved value of the token it stands for**, named in the comment
 * beside it, taken from `packages/ui/src/styles/`. If a token moves, these do not, and the card
 * row is then the one place on the screen with last season's grey in it — which is a real
 * maintenance cost and is recorded here rather than hidden. The first draft of this file used
 * Tailwind's stock slate and red, which are not this design system's colours at all and would
 * have put the provider's text a visibly different shade from every label beside it.
 *
 * Light palette only, deliberately: `apps/web` applies no `dark` class and uses no `dark:`
 * utility, so there is one palette to match. A member app that gains a dark theme has to pass a
 * second appearance in here, because nothing about an iframe follows a class on our `<html>`.
 */
export const STRIPE_ELEMENT_APPEARANCE = {
  base: {
    // `font-sans` — the handoff's own stack, so the digits match the labels around them.
    fontFamily: 'Montserrat, Gotham, "Helvetica Neue", Arial, sans-serif',
    // `text-sm` over the `h-10` box the panel draws, matching the labels beside it.
    fontSize: '14px',
    lineHeight: '24px',
    color: '#464148', // --text-body (--gray-700, the base gray)
    '::placeholder': { color: '#8a848e' }, // --text-subtle (--gray-400)
  },
  // --destructive (--status-danger). The provider's own invalid state, in our red rather than its.
  invalid: { color: '#c2003c', iconColor: '#c2003c' },
} as const;

/**
 * **Which of the four regions the provider draws into, and which one is ours** (`MEM-021` AC-2).
 *
 * Three, not four. Stripe Elements has hosted fields for the number, the expiry and the security
 * code, and **no element at all for a cardholder name** — its own integrations collect that in the
 * site's own input and pass it as billing details. Ruled by Sumit on 2026-08-30: build the name as
 * our own input rather than amend AC-2 or adopt the Address Element, which would collect a full
 * postal address nobody asked for.
 *
 * The four regions and their labels are unchanged — `WEB_CARD_FIELDS` is still all four and
 * `CARD_FIELD_LABELS.name` is still *"Name on card"*. What changed is who draws into the fourth.
 *
 * A name is safe for this application to hold in a way the other three are not, and the reason is
 * not that it looks harmless: it is **not chargeable** (`PAY-001` AC-4 lists *"card number,
 * security code or raw expiry entry"*; `MEM-021` AC-3 lists *"card number, expiry, CVC or PAN"* —
 * a name is in neither), and it **never crosses the port**, because it goes from the input into
 * the provider's script in the member's browser and stops. ADR 045's forbidden list, which does
 * name the cardholder name, governs traffic between an H2HSpark server and the provider; nothing
 * here is that.
 */
export const PROVIDER_DRAWN_FIELDS: ReadonlyArray<CardElementField> = [
  'number',
  'expiry',
  'security-code',
];

/**
 * What the member is shown in the name field before they type.
 *
 * The provider's three fields carry their own placeholders, drawn inside their own iframes; this
 * is the one field whose placeholder is ours to write.
 */
export const NAME_ON_CARD_PLACEHOLDER = 'As printed on the card';

/**
 * What a member reads when the provider's script could not be reached at all.
 *
 * Offline, a blocked third-party script, a content blocker. It is reported through the validity
 * channel as well as drawn in the regions, so the Pay control stays disabled and the reason sits
 * beside it — a live control over fields that do not exist is the failure this sentence prevents.
 */
export const CARD_ENTRY_UNAVAILABLE =
  'Card entry could not be loaded. Check your connection or any script blocker, then try again.';

/** What a member reads when the exchange failed and the provider said nothing usable about why. */
export const CARD_TOKEN_EXCHANGE_FAILED = 'Your card could not be checked. Try again in a moment.';

// ─── Tickets & orders, order detail and the receipt (`MEM-022` AC-3, AC-4, AC-5) ───────────────

/**
 * `MEM-022` AC-3's heading and its sub-line, **verbatim** from `MEM-TKT-012` and the prototype.
 *
 * Both are acceptance-criterion text rather than copy somebody chose, so they live here as
 * constants a test can name — the same reason `SERVICE_FEE_LABEL` and `TOTAL_LABEL` are constants
 * on the server rather than strings in a `<dt>`.
 */
export const TICKETS_AND_ORDERS_HEADING = 'Tickets & orders';
/**
 * **Reworded on 2026-09-03, and the reason is the list beneath it.**
 *
 * It read *"Every event you've bought a ticket to. Tap an order for full transaction details."* —
 * which was exactly what the screen served and exactly the gap Sumit closed: a member who attended
 * a free event was not on it. The list is registrations now, so the first sentence had to change
 * or the screen would be lying about its own rows.
 *
 * The second sentence stays true and is now stated as the control it became. **Receipt** opens the
 * order — the status badge, the transaction card, and the *View receipt* that prints the document
 * — and it is drawn only on a row that has one.
 */
export const TICKETS_AND_ORDERS_DESCRIPTION =
  "Every event you've registered for. Open Receipt for full transaction details.";

/** `MEM-022` AC-6's block, verbatim from `MEM-TKT-015`. Unchanged — it was right all along. */
export const YOUR_REGISTRATIONS_HEADING = 'Your registrations';
export const YOUR_REGISTRATIONS_DESCRIPTION = "Events you've registered for, with receipts.";

/**
 * What the list says to a member who has registered for nothing. Not an error — an answer.
 *
 * The old copy sent free registrations somewhere else — *"Free events appear on your event pages
 * instead"* — which was honest about a list that could not hold them and is now false. They are
 * here.
 */
export const NO_ORDERS_TITLE = 'Nothing here yet';
export const NO_ORDERS_DESCRIPTION =
  'Every event you register for lands here, free or paid, with its receipt when there is one.';

/** The two controls on a row (`MEM-022` AC-3, AC-6, ruled 2026-09-03). */
export const REGISTRATION_RECEIPT_LABEL = 'Receipt';
export const REGISTRATION_EVENT_LABEL = 'Event';

/** `MEM-022` AC-4's screen. "Order details", as `MEM-TKT-013` and the prototype have it. */
export const ORDER_DETAIL_HEADING = 'Order details';

/**
 * **Which tone an order's status badge is drawn in** (`MEM-022` AC-4, `MEM-023` AC-5).
 *
 * The *words* are served — `ORDER_STATUS_BADGES` on the API — and only the tone is chosen here,
 * because a colour is a property of this design system and not of an order.
 *
 * A total `Record` rather than the two ternaries this replaced, one on the order detail and one on
 * the summary panel. They read `status === 'refunded' ? 'muted' : 'success'`, which silently made
 * `refund-pending` green and reassuring on the two screens a member checks when their money has not
 * arrived. A record over {@link MemberOrderStatus} makes the next status a compile error on both
 * screens at once instead of a default nobody chose.
 *
 * `warning` for the pending state, `--warning-bg` on `--warning`: money in flight is neither done
 * nor wrong, and it is the one row on the list a member is meant to look at twice.
 */
export const ORDER_STATUS_BADGE_TONES = {
  paid: 'success',
  'refund-pending': 'warning',
  refunded: 'muted',
} as const satisfies Record<MemberOrderStatus, string>;

/**
 * The same record, plus the row that never involved money (`MEM-022` AC-3, AC-6).
 *
 * Spread rather than restated, so a paid row on the list is drawn in the same green as the order
 * detail it opens — and so the compile error {@link ORDER_STATUS_BADGE_TONES} exists to produce
 * fires on both screens at once rather than on whichever was remembered.
 *
 * `success` for `registered`: a free place is held, which is the same good news as a paid one. The
 * difference between the two rows is the badge's *word*, which is served, and the total beside it,
 * which a free row does not have.
 */
export const REGISTRATION_STATUS_BADGE_TONES = {
  ...ORDER_STATUS_BADGE_TONES,
  registered: 'success',
} as const satisfies Record<MemberRegistrationStatus, string>;

/** The tone of the second badge — the place given up, beside whatever the money did. */
export const CANCELLED_BADGE_TONE = 'muted' as const;

/** The Transaction card's heading (`MEM-022` AC-4, `MEM-TKT-013`). */
export const TRANSACTION_HEADING = 'Transaction';

/** The Payment card's heading on the order detail (`MEM-TKT-013`). */
export const PAYMENT_HEADING = 'Payment';

/** What a refunded order's payment card says came back (`MEM-023`, rendered here). */
export const REFUNDED_LABEL = 'Refunded';

/** The controls at the foot of the order detail (`MEM-TKT-013`). */
export const VIEW_TICKET_LABEL = 'View ticket';
export const VIEW_RECEIPT_LABEL = 'View receipt';

/** What the order detail says when the id names nothing this member may read (`MEM-022` AC-7). */
export const ORDER_NOT_FOUND_TITLE = 'We could not find that order';
export const ORDER_NOT_FOUND_DESCRIPTION =
  'Check the link, or find it again under Tickets & orders.';

/**
 * The receipt's own heading (`MEM-022` AC-5), and deliberately **not** `RECEIPT_HEADING`.
 *
 * `RECEIPT_HEADING` is "Your reservation" — `MEM-020`'s confirmation screen, reached straight
 * after checkout, which tells a member they have a place. This is the printable document
 * `MEM-TKT-014` describes: H2HSpark's own record of a transaction, filed for expenses or produced
 * in a dispute. Two artefacts, two headings, and merging them would leave one screen unable to be
 * honest about which it is.
 */
export const RECEIPT_DOCUMENT_HEADING = 'Receipt';

/** The receipt's field labels (`MEM-022` AC-5, `MEM-TKT-014`). */
export const RECEIPT_BILLED_TO_LABEL = 'Billed to';
export const RECEIPT_PAYMENT_DATE_LABEL = 'Payment date';
export const RECEIPT_EVENT_LABEL = 'Event';

/** The receipt's print control. Browser print, so there is no file to download and none to store. */
export const PRINT_RECEIPT_LABEL = 'Print';

/**
 * §2.16's second control on the receipt's bar.
 *
 * The prototype closes an overlay with it. Here the receipt is a **route** — it has to be
 * printable and linkable from an email — so Close is a link to the order detail: the screen
 * behind it, which is the useful "nothing" to go back to.
 */
export const RECEIPT_CLOSE_LABEL = 'Close';

/** Where the member's own order surfaces live. One function each, so no two links can disagree. */
export const TICKETS_AND_ORDERS_PATH = '/app/account/orders';

export function orderDetailPath(orderId: string): string {
  return `/app/account/orders/${encodeURIComponent(orderId)}`;
}

export function orderReceiptPath(orderId: string): string {
  return `/app/account/orders/${encodeURIComponent(orderId)}/receipt`;
}

/**
 * `{date and time} · {venue}` — `MEM-022` AC-4's second line, and the receipt's event block.
 *
 * Either half may be missing and the line still has to be true, so nothing is invented and no
 * placeholder dash stands in for an absent half. The ticket's `venueLine` takes the same position
 * for the same reason: a line reading "· " is worse than silence.
 *
 * **The date is not formatted here.** It arrives already rendered, by a caller that used the
 * event's own timezone — `docs/standards/time-and-timezone.md#display` — because a helper that
 * formatted an instant would need a zone parameter and a defaulted zone is how a Chicago event
 * ends up displayed in New York.
 */
export function whenAndWhereLine(when: string | null, venueName: string | null): string | null {
  const parts = [when, venueName].filter(
    (part): part is string => part !== null && part.trim().length > 0,
  );
  return parts.length === 0 ? null : parts.join(' · ');
}

/**
 * `Charged {date}` — the payment card's second line (`MEM-TKT-013`).
 *
 * Takes the already-rendered date for the reason {@link whenAndWhereLine} does.
 */
export function chargedOnLine(paidOn: string): string {
  return `Charged ${paidOn}`;
}

/**
 * The order number as a member reads it: `#K7M2P-QR4WX`.
 *
 * The `#` is here rather than in the JSX so that the ticket, the confirmed panel and these three
 * screens cannot come to prefix it differently. `MEM-017` AC-1 and `MEM-022` AC-1 both already
 * print this number with a `#`, and it is one number across five surfaces.
 */
export function orderNumberLine(orderNumber: string): string {
  return `#${orderNumber}`;
}

/**
 * **`Order #{n} · {price} · {card}`** — the third line of `Web App.dc.html` §2.15's registration
 * row.
 *
 * The prototype writes all three parts because its fixture always has all three. A real row often
 * does not: a free registration has no order and therefore no price and no card, and a place an
 * organizer entered at a paid event (`EVT-026`) has neither either. So the parts that exist are
 * joined and the rest are simply absent — never `$0.00`, which on that second row would tell a
 * member their ticket was free, and never a dash, which is a separator pretending to be a value.
 *
 * The order number is always there, which is what makes the line worth keeping in every case: it
 * is the string a member quotes when they write in.
 */
export function orderLine(parts: {
  readonly orderNumber: string;
  readonly price: string | null;
  readonly card: string | null;
}): string {
  return [`Order ${orderNumberLine(parts.orderNumber)}`, parts.price, parts.card]
    .filter((part): part is string => part !== null && part.trim().length > 0)
    .join(' · ');
}

/**
 * **The receipt's footer** (`MEM-022` AC-5, and the story's own Open Questions).
 *
 * `MEM-TKT-014`'s cell is clipped at *"and a footer …"*, and the story decided what it carries
 * rather than queuing it: the legal entity as merchant of record, the merchant-of-record sentence,
 * a support address, and the refund terms snapshotted on this order.
 *
 * Merchant of record is why each belongs. ADR 020 and `SYS-019` make H2HSpark the merchant, so the
 * receipt is **H2HSpark's document rather than the organizer's** — it is what a member files for
 * expenses or produces in a dispute, and it has to say who took the money and what the terms were
 * at the time.
 *
 * The **refund terms are not here**: they are `order.refundPolicy.sentence`, this order's own
 * snapshot, served by the API. That is the whole point of `EVT-024`'s snapshot and of
 * `AgreedRefundPolicy` being a different type from the event's live policy — a receipt that
 * restated today's terms would be a document about a deal nobody agreed to. The prototype's own
 * footer gets this wrong in the way `MEM-TKT-013`'s correction records: it hard-codes *"Full
 * refunds up to 48 hours before the event"*, one organizer's sample policy presented as a platform
 * rule.
 *
 * ## The legal entity line is pending counsel
 *
 * The story queues it under `OI-7` alongside the merchant-of-record advice, and says in as many
 * words that this is *"a wording question, not a build one, and the field is there either way"*.
 * The field is here. When counsel answers, this constant changes and no screen does.
 *
 * ## Why this is a client constant and the refund sentence is served
 *
 * The rule this codebase keeps is that every sentence whose *content depends on data* is served,
 * so the sentence a test asserts is the sentence a member gets — `REGISTRATION_REFUSALS`,
 * `SEATS_UNAVAILABLE_MESSAGES`, `REFUND_POLICY_NOT_PUBLISHED`. None of these three varies by
 * anything: they are the platform's own identity, the same on every receipt ever printed. Serving
 * them would be three fields on every order payload with one reader.
 */
export const RECEIPT_MERCHANT_OF_RECORD = {
  /** Pending counsel (`OI-7`). The registered name and address of the party that took the money. */
  legalEntity: 'H2HSpark, Inc. · Providence, Rhode Island, USA',
  statement: 'H2HSpark is the merchant of record for this transaction.',
  supportAddress: 'hello@h2hspark.com',
} as const;

// ─── choosing a ticket type ────────────────────────────────────────────────────────────────────

/**
 * The selector's heading, and the reason it is a heading rather than a label.
 *
 * `EVT-017` AC-3 gives the organizer's own list the heading *"Your tickets"* with the helper
 * *"Members see these in the order you list them."* This is the member's side of that same list,
 * and it names the decision rather than the noun — a member at checkout is choosing, not
 * browsing.
 */
export const TICKET_CHOICE_HEADING = 'Choose your ticket';

/**
 * The radio group's accessible name.
 *
 * `nfrs: accessibility#wcag-aa`. The group needs a name a screen reader announces before the
 * options, and the visible heading is that name — bound with `aria-labelledby` rather than
 * duplicated, so the two cannot drift.
 */
export const TICKET_CHOICE_GROUP_LABEL = TICKET_CHOICE_HEADING;

/**
 * One option's accessible label: the ticket type and its price, in one string.
 *
 * The price is the **server's** rendering (`TicketChoice.price`, produced by `formatCents` in
 * integer arithmetic), never a number this app formats — the same rule `reserveActionLabel`
 * follows, and the reason a test may assert these bytes on a machine whose Node version differs
 * from CI's.
 */
export function ticketChoiceLabel(name: string, price: string): string {
  return `${name} · ${price}`;
}

/**
 * What a member is told once they have chosen.
 *
 * `MEM-020` AC-1 fixes quantity at one and says so on the pinned bar; repeating it beside the
 * selector is the same statement in the place where somebody who has just picked a tier might
 * otherwise look for a quantity control. It is a sentence and not a control, per
 * `MEM-TKT-001`'s correction.
 */
export const ONE_TICKET_WHICHEVER_YOU_CHOOSE = ONE_TICKET_PER_PERSON;

// ─── the pinned bar on an event that sells more than one ticket (`MEM-048` AC-9) ───────────────

/*
 * **Why this section is here and not up beside `reserveActionLabel`.**
 *
 * The multi-tier bar borrows the selector's own heading (`TICKET_CHOICE_HEADING`, just above) for
 * the line under the button, so that the sentence a member reads on the event page and the
 * heading they meet one tap later are one string rather than two that agree today. That is the
 * whole of the dependency, and it points this way, so the code sits after the thing it reads.
 */

/**
 * The word that turns *a* price into *the cheapest of several* (`MEM-048` AC-9).
 *
 * Sumit's ruling of 2026-08-29, in one word. A three-tier event running $20–$50 read **"$20"** on
 * the pinned bar, because the bar was handed `ticketTypes[0]` and had no way to know there was a
 * `ticketTypes[1]`. A member tapped a price and reached a checkout where the tier they wanted
 * cost $50: the number they were shown was true of one row and read as true of the event.
 *
 * "from" is the smallest honest repair. It does not promise the tier the checkout will default to
 * — that is still the organizer's first, `MEM-048` AC-2 — it promises only that nothing on this
 * event is cheaper, which is exactly what the cheapest row guarantees.
 */
export const FROM_PRICE_PREFIX = 'from';

/**
 * `"$20.00"` → `"from $20.00"`.
 *
 * Separate from {@link reserveActionLabel} on purpose: the label's two wordings are `MEM-020`
 * AC-1 and AC-6 and are quoted verbatim in that story's tagged test, so what this story adds is a
 * **different price phrase to interpolate**, not a third label. `reserveActionLabel` is still the
 * one place "Reserve my spot · " is spelled, and a single-tier event still gets the byte-identical
 * string it got before this existed.
 *
 * `price` is the server's rendering (`formatCents`, integer division on the API). This
 * concatenates; it never formats an amount and never does arithmetic on one.
 */
export function fromPriceWord(price: string): string {
  return `${FROM_PRICE_PREFIX} ${price}`;
}

/** What a free event's ticket is called on the bar — the specification's own default,
 * *"Ticket type is General admission unless the organizer defined others"*, and the same name the
 * server prices a free order under, so the bar and the order summary a tap later name one thing. */
export const DEFAULT_TICKET_TYPE_NAME = 'General admission';

/**
 * The line under the button when there is more than one tier to choose between.
 *
 * A single-tier bar names the ticket, because there is one to name. Naming
 * `ticketTypes[0]` under a **"from"** price drawn from some other row would be the original bug
 * wearing a hedge: the member would read "Early bird" beside a price that is General's, or the
 * reverse. So the multi-tier line names the *decision* instead, in the selector's own words.
 */
export const CHOOSE_YOUR_TICKET_LINE = `${TICKET_CHOICE_HEADING} · ${ONE_TICKET_PER_PERSON}`;

/** The shape the bar needs off a ticket type: the integer to compare and the string to render.
 * Structural, so both `PublicTicketType` (the event payload) and `TicketChoice` (the checkout's)
 * satisfy it without either domain importing the other's. */
export interface ReserveBarTicketType {
  readonly name: string;
  readonly priceCents: number;
  readonly price: string;
}

/**
 * The cheapest ticket type, or `null` when the event sells none.
 *
 * **No money arithmetic happens here.** `priceCents` is compared, never summed, divided or
 * averaged, and what is rendered is the winning row's own `price` string — the server's
 * `formatCents` output, carried through untouched. Picking a minimum is a *selection* among
 * amounts the API already rendered, which is why the "from" price is a price the member can
 * actually pay rather than a number this app computed.
 *
 * First wins on a tie, which keeps the organizer's order (`EVT-017` AC-4) as the tiebreak rather
 * than introducing a second opinion about it.
 */
export function cheapestTicketType<T extends { readonly priceCents: number }>(
  ticketTypes: ReadonlyArray<T>,
): T | null {
  let cheapest: T | null = null;
  for (const ticketType of ticketTypes) {
    if (cheapest === null || ticketType.priceCents < cheapest.priceCents) cheapest = ticketType;
  }
  return cheapest;
}

/** The two strings the pinned bar renders. Returned together, and as **parts** rather than as one
 * joined blob, because the button and the line beneath it are two elements and a caller that had
 * to split a string to fill them would be the place a separator drifts. */
export interface ReserveActionCopy {
  readonly label: string;
  readonly line: string;
}

/**
 * What the pinned reserve bar says, decided from the event's **whole** ticket-type list
 * (`MEM-020` AC-1 and AC-6, `MEM-048` AC-9).
 *
 * Three cases, and only the third is new:
 *
 * | The event sells | Label | Line |
 * | --- | --- | --- |
 * | no ticket types (free) | `Reserve my spot · Free` | `General admission · One ticket per person.` |
 * | one ticket type | `Reserve my spot · $25.00` | `Early bird · One ticket per person.` |
 * | two or more | `Reserve my spot · from $20.00` | `Choose your ticket · One ticket per person.` |
 *
 * The first two rows are `MEM-020` AC-1 and AC-6 and are produced by calling that story's own
 * functions, unchanged — this function chooses which of them applies rather than restating either,
 * so the bytes its tagged test pins cannot drift out from under it.
 *
 * ## A free tier alongside paid ones anchors at `$0.00`, not at "Free"
 *
 * `EVT-017` permits it and `paidEventHasAPricedTicketType` says so in as many words: *"nothing in
 * `EVT-017` forbids a free tier on a paid event"*. So the cheapest row can be zero, and there are
 * two things the bar could say.
 *
 * It says **"from $0.00"**, for two reasons that point the same way. The first is that `$0.00` is
 * what the member is about to read: the selector one tap later renders `choice.price` for every
 * row, and that row's `choice.price` is `$0.00` — a bar saying "Free" and a row saying "$0.00"
 * would be two renderings of one amount, which is the failure `PublicTicketType` carries both a
 * number and a string to prevent. The second is that `MEM-020` AC-6 ties the word **Free** to a
 * specific promise: *"a free event's reserve action reads 'Reserve my spot · Free' **and checkout
 * collects no payment method**"*. This event does collect one, for its paid tiers. Borrowing the
 * word would borrow the promise and break it.
 */
export function reserveActionCopy(
  ticketTypes: ReadonlyArray<ReserveBarTicketType>,
): ReserveActionCopy {
  const cheapest = cheapestTicketType(ticketTypes);
  // A free event carries no ticket types at all (`EVT-017` AC-2) — `MEM-020` AC-6, untouched.
  if (cheapest === null) {
    return {
      label: reserveActionLabel(null),
      line: reserveTicketTypeLine(DEFAULT_TICKET_TYPE_NAME),
    };
  }
  // One ticket type is not a range. `MEM-020` AC-1's exact wording, byte for byte — the whole
  // point of the ruling is that this row did not change.
  if (ticketTypes.length < 2) {
    return {
      label: reserveActionLabel(cheapest.price),
      line: reserveTicketTypeLine(cheapest.name),
    };
  }
  return {
    label: reserveActionLabel(fromPriceWord(cheapest.price)),
    line: CHOOSE_YOUR_TICKET_LINE,
  };
}

/**
 * **The price at the head of the event page's sidebar** — `Web App.dc.html` §2.2: the amount at
 * `800/var(--text-2xl)` beside *"per seat"* (`MEM-056` AC-4).
 *
 * Decided from the same whole list, by the same rule, as {@link reserveActionCopy}, so the price
 * above the button and the price on it cannot disagree: one row's own served `price`, *"from"*
 * the cheapest when there are several (`MEM-048` AC-9), and `MEM-TKT-005`'s **Free** with no unit
 * when the event sells nothing — "Free per seat" would be a price for something that has none.
 *
 * The prototype's *"2 seats left of 20"* beneath it is not drawn: no member payload carries a
 * seat count and no story puts one on this page (see the PR's *not built*).
 */
export const PER_SEAT = 'per seat';

export function sidebarPrice(ticketTypes: ReadonlyArray<ReserveBarTicketType>): {
  readonly amount: string;
  readonly unit: string | null;
} {
  const cheapest = cheapestTicketType(ticketTypes);
  if (cheapest === null) return { amount: FREE_PRICE_WORD, unit: null };
  return {
    amount: ticketTypes.length < 2 ? cheapest.price : fromPriceWord(cheapest.price),
    unit: PER_SEAT,
  };
}
